/* NOTE: Both lines are required due to current React version */
/* eslint-disable react/no-find-dom-node */
/* eslint-disable react/no-string-refs */
import * as React from 'react';
import { Component, deps, inject, observer } from '../lib/component';
import { ClientModel } from '../modules/client/model';
import { ClientController } from '../modules/client/controller';
import CheckoutCardList from '../components/checkout-v2/checkout-card-list';
import { CheckoutSummaryPanel } from '../components/checkout-v2/checkout-summary-panel';

import { styles } from '../components/checkout-v2/styles';
import { ImageModel } from '../modules/image/model';
import { EulaModal } from '../components/checkout-v2/eula-modal/eula-modal';
import { Loading } from '../components/loading/loading';
import { classes } from 'typestyle';
import { AuthModal } from '../components/user/auth-modal';
import CheckoutUserPanel from '../components/checkout-v2/checkout-user-panel';
import { CheckoutActions } from '../components/checkout-v2/checkout-actions';
import { RightsholderModal } from '../components/checkout-v2/rightsholder-modal';
import { TrackSchema } from '../types/schema';
import { SVGIcon } from '../components/svg-icon';
import { ButtonPill } from '../components/button-pill';
import { colorBlack } from '../theme/color';
import { ORDER_STATE } from '../modules/checkout/model';
import { isTopOfElementVisible } from '../lib/DOMHelpers';
import { unwrapMobxComponent } from '../lib/helpers';

export interface CheckoutContainerProps {
  model: ClientModel;
  controller: ClientController;
}

@inject(deps)
@observer
export class CheckoutV2Container extends Component<CheckoutContainerProps> {
  coverUrlProvider: ImageModel['cover'];

  checkoutSummary: HTMLElement;

  constructor(props) {
    super(props);

    this.coverUrlProvider = props.model.image.cover.bind(props.model.image.cover);

    this.state = {
      eula: false,
      rightsholders: false,
      hasRightsholders: true,
      error: false,
      showChannelDetails: false,
      showEulaModal: false,
      eulaTrack: null,
      showRightsholderModal: false,
      rightsholderTrack: null,
      showAuthModal: false,
    };
  }

  componentDidMount() {
    // Sledgehammer way of ensuring we have the latest version of the basket
    this.props.controller.checkout.cancelOrder();
    this.checkForOrder();
    this.checkForRightsholders();
  }

  componentDidUpdate() {
    this.checkForOrder();
    this.checkForRightsholders();
  }

  handleChannelToggle = (event) => {
    this.setState({ showChannelDetails: !this.state.showChannelDetails });
  };

  executeScroll = () =>
    this.checkoutSummary.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

  handleCheckout = (event) => {
    event.preventDefault();

    const { user } = this.props.model;
    // Prompt the user to sign in before checking checkbox state
    if (!user.user) {
      this.showAuth();
      return;
    }

    const { eula, rightsholders } = this.state;

    if (!eula || !rightsholders) {
      this.setState({ error: true, showChannelDetails: false });
      if (!isTopOfElementVisible(this.checkoutSummary, 50)) {
        this.executeScroll();
      }
    } else {
      this.setState({ error: false });
      this.props.controller.checkout.goToDownload();
    }
  };

  handleCheckboxChange = (type, checked) => {
    const { eula, rightsholders } = this.state;
    if (eula || rightsholders) {
      this.setState({ error: false });
    }
    this.setState({ [type]: !checked });
  };

  async handleRemoveTrack(identity: string) {
    await this.props.controller.checkout.removeTrack(identity);
    this.props.controller.checkout.cancelOrder();
  }

  showEula(track: TrackSchema) {
    this.props.controller.page.checkout.loadEulaInfo(track);
    this.setState({
      showEulaModal: true,
      eulaTrack: track,
    });
  }

  hideEula() {
    this.setState({
      showEulaModal: false,
    });
  }

  showRightsholder(track?: TrackSchema) {
    this.setState({
      showRightsholderModal: true,
      rightsholderTrack: track,
    });

    let trackSummary;
    let wasGlobalTrigger = true;
    if (typeof track === 'object' && track !== null) {
      const { identity, title, slug } = track;
      trackSummary = { identity, title, slug };
      wasGlobalTrigger = false;
    }
    this.props.controller.analytics.sendMixpanel('User clicks rightsholder modal', {
      track: trackSummary,
      wasGlobalTrigger,
    });
  }

  hideRightsholder() {
    this.setState({
      showRightsholderModal: false,
    });
  }

  showAuth() {
    this.setState({
      showAuthModal: true,
    });
  }

  hideAuth() {
    this.setState({
      showAuthModal: false,
    });
  }

  checkForOrder() {
    const { model } = this.props;
    const { user, basket, coupon, checkout } = model;
    if (
      user.user &&
      basket.channel !== null &&
      basket.tracks.length > 0 &&
      !coupon.loading &&
      checkout.orderState === ORDER_STATE.EMPTY
    ) {
      this.props.controller.checkout.deferredLoadOrder();
    }
  }

  checkForRightsholders() {
    const { basket } = this.props.model;
    const { hasRightsholders: currentHasRightsholders, rightsholders: currentRightsholders } = this.state;

    const hasRightsholders = basket.tracks.some((track) => track.reporting_party.slug === 'umg');

    if (currentHasRightsholders === hasRightsholders) return;

    this.setState({
      hasRightsholders,
      rightsholders: !hasRightsholders,
    });
  }

  render() {
    const {
      eula,
      rightsholders,
      hasRightsholders,
      error,
      showChannelDetails,
      showEulaModal,
      eulaTrack,
      showRightsholderModal,
      rightsholderTrack,
      showAuthModal,
    } = this.state;
    const { model } = this.props;
    const { user, basket, coupon: couponModel, checkout: checkoutModel } = model;
    const { orderState } = checkoutModel;
    const { handleClickLink } = this.props.controller.ui;
    const { analytics, ui, onboarding, checkout: checkoutController } = this.props.controller;

    if (process.env.SERVER) return <Loading className={styles.loading} text="Loading basket..." />;

    if (basket.tracks.length === 0) {
      return (
        <div className={styles.empty}>
          <SVGIcon.Basket color={colorBlack.toHexString()} />
          <p>Your basket is empty</p>
          <ButtonPill href="/search" onClick={handleClickLink}>
            Search music
          </ButtonPill>
        </div>
      );
    }

    if (user.user) {
      if (basket.channel === null) return <Loading className={styles.loading} text="Loading channel information..." />;
      if (couponModel.loading) return <Loading className={styles.loading} text="Loading coupon information..." />;
      // if (checkout.order === null) return <Loading className={styles.loading} text="Loading order information..." />;
    }

    const ready = !user.user || orderState === ORDER_STATE.READY;

    return (
      <div>
        <CheckoutActions
          className={styles.stickyHeader}
          orderReady={ready}
          showVat={false}
          eula={eula}
          rightsholders={rightsholders}
          handleCheckout={this.handleCheckout}
        />
        <AuthModal
          model={{ ...model.onboarding.signUpModal, show: showAuthModal }}
          onClickSignUp={ui.openSignInWindow}
          onRequestClose={this.hideAuth.bind(this)}
          onClickTermsConditions={onboarding.showTermsConditionsModal}
          onClickPrivacyPolicy={onboarding.showPrivacyPolicyModal}
          i18n={model.content.signUpModalI18n}
        />
        <RightsholderModal
          show={showRightsholderModal}
          title="Additional Rightsholder"
          ratecard={basket.ratecard}
          eulaContent={null}
          onRequestClose={this.hideRightsholder.bind(this)}
        />
        <EulaModal
          show={showEulaModal}
          title={'End User License Agreement'}
          track={eulaTrack}
          onRequestClose={this.hideEula.bind(this)}
        />
        <div className={classes(styles.container, user.user && 'with-user')}>
          <CheckoutUserPanel
            className={styles.userInfo}
            handleSignIn={this.showAuth.bind(this)}
            handleChannelToggle={this.handleChannelToggle.bind(this)}
            showToggle={showChannelDetails}
          />
          <CheckoutCardList
            model={model.basket}
            coverUrlProvider={this.coverUrlProvider}
            onClickLink={handleClickLink}
            onRemoveTrack={this.handleRemoveTrack.bind(this)}
            handleEulaRequest={this.showEula.bind(this)}
            handleRightsholderRequest={this.showRightsholder.bind(this)}
            analytics={analytics}
          />
          <CheckoutSummaryPanel
            userModel={user}
            checkoutModel={checkoutModel}
            hasRightsholders={hasRightsholders}
            eula={eula}
            rightsholders={rightsholders}
            error={error}
            handleCheckboxChange={this.handleCheckboxChange}
            handleRightsholderRequest={this.showRightsholder.bind(this)}
            handleCheckout={this.handleCheckout}
            ref={(ref) => {
              const finalRef = unwrapMobxComponent(ref);
              const div = finalRef && finalRef.summaryDiv;
              if (div) {
                this.checkoutSummary = div;
              }
            }}
          />
        </div>
      </div>
    );
  }
}
